/* =========================================================
   VELMORA — Global theme behavior
   Vanilla JS only. No framework. Progressive enhancement.
   ========================================================= */
(function () {
  "use strict";

  window.Velmora = window.Velmora || {};

  /* ---------------- Money formatting (Shopify-compatible) ---------------- */
  function formatMoney(cents, format) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || window.Velmora.moneyFormat || "{{amount}}";
    var value = "";

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? "," : thousands;
      decimal = decimal == null ? "." : decimal;
      if (isNaN(number) || number == null) return "0";
      number = (number / 100.0).toFixed(precision);
      var parts = number.split(".");
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      var frac = parts[1] ? decimal + parts[1] : "";
      return whole + frac;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    switch (match[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }
  window.Velmora.formatMoney = formatMoney;

  /* ---------------- Focus trap ---------------- */
  function trapFocus(container, initialFocus) {
    var selector =
      'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    var focusable = Array.prototype.filter.call(
      container.querySelectorAll(selector),
      function (el) {
        return el.offsetParent !== null;
      }
    );
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    (initialFocus || first).focus();

    function onKeydown(e) {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    container.addEventListener("keydown", onKeydown);
    return function () {
      container.removeEventListener("keydown", onKeydown);
    };
  }
  window.Velmora.trapFocus = trapFocus;

  /* ---------------- Drawer / panel system ---------------- */
  var releaseFocusTrap = null;
  var returnFocusEl = null;

  function anyPanelOpen() {
    return document.querySelector(".drawer.is-open, .filter-panel.is-open, .search-panel.is-open, .zoom-lightbox.is-open");
  }

  /* ---------------- Scroll lock ----------------
     Plain `overflow:hidden` on the body does not reliably stop touch/
     rubber-band scrolling on iOS Safari — the page behind a drawer can
     still creep. Pinning the body with `position:fixed` at its current
     offset (and restoring scrollTop on unlock) is the technique that
     actually holds, on every engine, while still letting an internal
     `overflow-y:auto` panel (like the reviews drawer body) scroll freely. */
  var lockedScrollY = 0;
  var scrollLocked = false;
  function lockScroll() {
    if (scrollLocked) return;
    scrollLocked = true;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = -lockedScrollY + "px";
    document.body.classList.add("no-scroll");
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.body.classList.remove("no-scroll");
    document.body.style.top = "";
    window.scrollTo(0, lockedScrollY);
  }

  function openDrawer(id, trigger) {
    var el = document.getElementById(id);
    if (!el) return;
    var overlay = document.querySelector('[data-drawer-overlay="' + id + '"]');
    el.classList.add("is-open");
    if (overlay) overlay.classList.add("is-open");
    lockScroll();
    el.setAttribute("aria-hidden", "false");
    returnFocusEl = trigger || document.activeElement;
    if (releaseFocusTrap) releaseFocusTrap();
    releaseFocusTrap = trapFocus(el);
    el.dispatchEvent(new CustomEvent("drawer:open"));
  }

  function closeDrawer(id) {
    var el = document.getElementById(id);
    if (!el) return;
    var overlay = document.querySelector('[data-drawer-overlay="' + id + '"]');
    el.classList.remove("is-open");
    if (overlay) overlay.classList.remove("is-open");
    el.setAttribute("aria-hidden", "true");
    if (!anyPanelOpen()) unlockScroll();
    if (releaseFocusTrap) {
      releaseFocusTrap();
      releaseFocusTrap = null;
    }
    if (returnFocusEl && typeof returnFocusEl.focus === "function") {
      returnFocusEl.focus();
      returnFocusEl = null;
    }
    el.dispatchEvent(new CustomEvent("drawer:close"));
  }

  function closeAllPanels() {
    var open = document.querySelectorAll(".drawer.is-open, .filter-panel.is-open");
    Array.prototype.forEach.call(open, function (d) {
      closeDrawer(d.id);
    });
    closeSearch();
  }

  window.Velmora.openDrawer = openDrawer;
  window.Velmora.closeDrawer = closeDrawer;


  /* ---------------- Horizontal sliders ----------------
     The track is a native scroll-snap row, so touch, trackpad and keyboard
     already work. This adds two things a pointer user expects: arrows that
     appear only when there is somewhere to go, and click-drag to pull the
     row along. Drag is deliberately suppressed for a click that never
     moved, so tapping a product card still opens it. */
  function initSliders(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll("[data-slider]"), function (wrap) {
      if (wrap.dataset.sliderReady === "true") return;
      var track = wrap.querySelector("[data-slider-track]");
      if (!track) return;
      wrap.dataset.sliderReady = "true";

      var prev = wrap.querySelector("[data-slider-prev]");
      var next = wrap.querySelector("[data-slider-next]");
      var rtl = document.documentElement.getAttribute("dir") === "rtl";

      function step() {
        var first = track.firstElementChild;
        var width = first ? first.getBoundingClientRect().width : 300;
        return Math.max(width + 20, track.clientWidth * 0.8);
      }

      function refresh() {
        var overflowing = track.scrollWidth - track.clientWidth > 4;
        // scrollLeft is negative in RTL in every current engine.
        var pos = Math.abs(track.scrollLeft);
        var max = track.scrollWidth - track.clientWidth - 4;
        if (prev) prev.hidden = !overflowing || pos <= 4;
        if (next) next.hidden = !overflowing || pos >= max;
      }

      function scrollBy(dir) {
        var amount = step() * dir * (rtl ? -1 : 1);
        track.scrollBy({ left: amount, behavior: "smooth" });
      }

      if (prev) prev.addEventListener("click", function () { scrollBy(-1); });
      if (next) next.addEventListener("click", function () { scrollBy(1); });
      // A touch/trackpad fling can fire this dozens of times a second;
      // rAF-batching keeps the arrow-visibility check from running more
      // than once per frame during that burst.
      var refreshTicking = false;
      function scheduleRefresh() {
        if (refreshTicking) return;
        refreshTicking = true;
        window.requestAnimationFrame(function () {
          refreshTicking = false;
          refresh();
        });
      }
      track.addEventListener("scroll", scheduleRefresh, { passive: true });
      window.addEventListener("resize", scheduleRefresh, { passive: true });
      refresh();

      var down = false, moved = false, startX = 0, startScroll = 0;
      track.addEventListener("pointerdown", function (e) {
        if (e.pointerType !== "mouse") return;
        down = true;
        moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
      });
      track.addEventListener("pointermove", function (e) {
        if (!down) return;
        var delta = e.clientX - startX;
        if (Math.abs(delta) > 4) {
          moved = true;
          track.classList.add("is-dragging");
        }
        track.scrollLeft = startScroll - delta;
      });
      function release() {
        down = false;
        track.classList.remove("is-dragging");
      }
      track.addEventListener("pointerup", release);
      track.addEventListener("pointercancel", release);
      track.addEventListener("pointerleave", release);
      // A drag that ended over a card must not also follow its link.
      track.addEventListener("click", function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      }, true);

      // Optional slow auto-advance (opt-in via data-slider-autoplay="<ms>",
      // used by the Custom Name carousel — the Pearls slider elsewhere
      // doesn't set this attribute, so it's unaffected). Stops for good at
      // the first real interaction — drag, swipe, wheel, or an arrow click
      // — rather than pausing and resuming, so it never fights a visitor
      // who has already taken control themselves. setInterval rather than
      // requestAnimationFrame deliberately: a several-second cadence has no
      // need for frame-perfect timing, and unlike rAF it keeps firing even
      // when the tab hasn't been actively repainted.
      var autoplayMs = parseInt(wrap.getAttribute("data-slider-autoplay"), 10);
      if (autoplayMs) {
        var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        var autoplayTimer = null;

        // True infinite loop (opt-in via data-slider-loop, used only by the
        // Custom Name carousel — no other slider sets this attribute, so
        // this whole block is a no-op everywhere else). The real cards are
        // cloned once and appended after themselves, so there's always
        // more track to scroll forward into. A light poll (setInterval
        // rather than a scroll listener, so it doesn't depend on scroll
        // events firing at a particular rate) silently subtracts one
        // real-set-width from scrollLeft the moment the visitor has
        // scrolled a full set past the start — whether that motion came
        // from autoplay or a manual swipe/drag. That reassignment is a
        // hard, instant jump rather than a smooth one, but it lands on a
        // pixel-identical frame — the clone is an exact duplicate of the
        // real set — so nothing visibly moves. Clones are marked
        // aria-hidden/tabindex="-1" (kept out of the a11y tree and tab
        // order, since they'd otherwise announce the same 7 products
        // twice) but keep their real href, so a click on one still opens
        // the correct product.
        var isLoop = wrap.hasAttribute("data-slider-loop");
        if (isLoop && track.children.length) {
          var realSetWidth = track.scrollWidth;
          Array.prototype.slice.call(track.children).forEach(function (card) {
            var clone = card.cloneNode(true);
            clone.setAttribute("aria-hidden", "true");
            clone.setAttribute("tabindex", "-1");
            var cloneImg = clone.querySelector("img");
            if (cloneImg) cloneImg.loading = "lazy";
            track.appendChild(clone);
          });
          setInterval(function () {
            var pos = Math.abs(track.scrollLeft);
            if (pos >= realSetWidth) {
              track.scrollLeft = (rtl ? -1 : 1) * (pos - realSetWidth);
            }
          }, 120);
        }

        function advanceAuto() {
          if (isLoop) {
            scrollBy(1);
            return;
          }
          var max = track.scrollWidth - track.clientWidth - 4;
          if (Math.abs(track.scrollLeft) >= max) {
            track.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            scrollBy(1);
          }
        }
        function stopAutoplayForGood() {
          if (autoplayTimer) {
            clearInterval(autoplayTimer);
            autoplayTimer = null;
          }
          track.removeEventListener("pointerdown", stopAutoplayForGood);
          track.removeEventListener("touchstart", stopAutoplayForGood);
          wrap.removeEventListener("wheel", stopAutoplayForGood);
          if (prev) prev.removeEventListener("click", stopAutoplayForGood);
          if (next) next.removeEventListener("click", stopAutoplayForGood);
        }

        if (!prefersReducedMotion) {
          track.addEventListener("pointerdown", stopAutoplayForGood, { passive: true });
          track.addEventListener("touchstart", stopAutoplayForGood, { passive: true });
          wrap.addEventListener("wheel", stopAutoplayForGood, { passive: true });
          if (prev) prev.addEventListener("click", stopAutoplayForGood);
          if (next) next.addEventListener("click", stopAutoplayForGood);
          autoplayTimer = setInterval(advanceAuto, autoplayMs);
        }
      }
    });
  }
  window.Velmora.initSliders = initSliders;

  /* ---------------- Search panel ---------------- */
  function openSearch() {
    var panel = document.getElementById("search-panel");
    if (!panel) return;
    panel.classList.add("is-open");
    panel.setAttribute("aria-hidden", "false");
    lockScroll();
    var input = panel.querySelector("[data-predictive-input]");
    if (input) {
      setTimeout(function () {
        input.focus();
      }, 260);
    }
  }

  function closeSearch() {
    var panel = document.getElementById("search-panel");
    if (!panel || !panel.classList.contains("is-open")) return;
    panel.classList.remove("is-open");
    panel.setAttribute("aria-hidden", "true");
    if (!anyPanelOpen()) unlockScroll();
  }

  /* ---------------- Global delegated events ---------------- */
  document.addEventListener("click", function (e) {
    var openTrigger = e.target.closest("[data-drawer-target]");
    if (openTrigger) {
      e.preventDefault();
      openDrawer(openTrigger.getAttribute("data-drawer-target"), openTrigger);
      return;
    }

    var closeBtn = e.target.closest("[data-drawer-close]");
    if (closeBtn) {
      var panel = closeBtn.closest(".drawer, .filter-panel, .zoom-lightbox");
      if (panel) {
        if (panel.classList.contains("zoom-lightbox")) {
          panel.classList.remove("is-open");
          if (!anyPanelOpen()) unlockScroll();
        } else {
          closeDrawer(panel.id);
        }
      }
      return;
    }

    if (e.target.closest("[data-search-open]")) {
      e.preventDefault();
      openSearch();
      return;
    }
    if (e.target.closest("[data-search-close]")) {
      e.preventDefault();
      closeSearch();
      return;
    }

    if (e.target.classList && e.target.classList.contains("drawer__overlay")) {
      closeAllPanels();
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      closeAllPanels();
      var lightbox = document.querySelector(".zoom-lightbox.is-open");
      if (lightbox) {
        lightbox.classList.remove("is-open");
        if (!anyPanelOpen()) unlockScroll();
      }
      document.querySelectorAll(".site-header__nav-item.is-open").forEach(function (i) {
        i.classList.remove("is-open");
      });
    }
  });

  /* ---------------- Sticky header ---------------- */
  function initHeaderScroll() {
    var header = document.querySelector("[data-site-header]");
    if (!header) return;
    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      header.classList.toggle("is-scrolled", y > 12);
      if (y > lastY && y > 200 && !anyPanelOpen()) {
        header.classList.add("is-hidden");
      } else {
        header.classList.remove("is-hidden");
      }
      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* ---------------- Desktop mega menu ---------------- */
  function initMegaMenu() {
    var items = document.querySelectorAll(".site-header__nav-item[data-has-menu]");
    if (!items.length) return;
    var closeTimer;
    var isTouch = window.matchMedia("(hover: none)").matches;

    items.forEach(function (item) {
      var link = item.querySelector(".site-header__nav-link");

      function open() {
        clearTimeout(closeTimer);
        items.forEach(function (i) {
          if (i !== item) i.classList.remove("is-open");
        });
        item.classList.add("is-open");
        if (link) link.setAttribute("aria-expanded", "true");
      }
      function scheduleClose() {
        closeTimer = setTimeout(function () {
          item.classList.remove("is-open");
          if (link) link.setAttribute("aria-expanded", "false");
        }, 160);
      }

      if (!isTouch) {
        item.addEventListener("mouseenter", open);
        item.addEventListener("mouseleave", scheduleClose);
      }

      if (link) {
        link.addEventListener("focus", open);
        // On touch devices the first tap opens the panel, the second follows
        // the link — so a category link is still reachable.
        link.addEventListener("click", function (e) {
          if (isTouch && !item.classList.contains("is-open")) {
            e.preventDefault();
            open();
          }
        });
      }

      item.addEventListener("focusout", function (e) {
        if (!item.contains(e.relatedTarget)) {
          item.classList.remove("is-open");
          if (link) link.setAttribute("aria-expanded", "false");
        }
      });
    });

    document.addEventListener("click", function (e) {
      items.forEach(function (item) {
        if (!item.contains(e.target)) item.classList.remove("is-open");
      });
    });
  }

  /* ---------------- Mobile nav accordions ---------------- */
  function initMobileNav() {
    document.querySelectorAll("[data-mobile-nav-trigger]").forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        var item = trigger.closest(".mobile-nav__item");
        if (!item) return;
        var wasOpen = item.classList.contains("is-open");
        var siblings = item.parentElement.querySelectorAll(".mobile-nav__item");
        siblings.forEach(function (i) {
          i.classList.remove("is-open");
          var t = i.querySelector("[data-mobile-nav-trigger]");
          if (t) t.setAttribute("aria-expanded", "false");
        });
        if (!wasOpen) {
          item.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        }
      });
    });
  }

  /* ---------------- Accordions ---------------- */
  function initAccordions() {
    document.querySelectorAll(".accordion__trigger").forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        var item = trigger.closest(".accordion__item");
        if (!item) return;
        item.classList.toggle("is-open");
        trigger.setAttribute("aria-expanded", item.classList.contains("is-open"));
      });
    });
  }

  /* ---------------- Quantity steppers (non-cart) ---------------- */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-qty-step]");
    if (!btn) return;
    var wrap = btn.closest(".qty-stepper");
    if (!wrap) return;
    var input = wrap.querySelector("input");
    if (!input) return;
    var step = parseInt(btn.getAttribute("data-qty-step"), 10);
    var min = parseInt(input.getAttribute("min") || "1", 10);
    var value = parseInt(input.value || "1", 10) + step;
    if (isNaN(value) || value < min) value = min;
    input.value = value;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------------- Sort panel ---------------- */
  function initCollectionToolbar() {
    var sortTrigger = document.querySelector("[data-sort-trigger]");
    var sortPanel = document.querySelector("[data-sort-panel]");
    if (!sortTrigger || !sortPanel) return;
    sortTrigger.addEventListener("click", function (e) {
      e.stopPropagation();
      sortPanel.classList.toggle("is-open");
    });
    document.addEventListener("click", function (e) {
      if (!sortPanel.contains(e.target) && !sortTrigger.contains(e.target)) {
        sortPanel.classList.remove("is-open");
      }
    });
  }

  /* ---------------- Announcement bar ---------------- */
  function initAnnouncementBar() {
    var track = document.querySelector("[data-announcement-track]");
    if (!track) return;
    var slides = track.querySelectorAll(".announcement-bar__slide");
    if (slides.length < 2) return;
    var index = 0;
    var prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var timer = null;

    function show(i) {
      slides.forEach(function (s, si) {
        s.hidden = si !== i;
      });
    }
    function step(dir) {
      index = (index + dir + slides.length) % slides.length;
      show(index);
      restart();
    }
    function restart() {
      if (prefersReduced) return;
      clearInterval(timer);
      timer = setInterval(function () {
        index = (index + 1) % slides.length;
        show(index);
      }, 5000);
    }

    show(0);
    restart();

    var nextBtn = document.querySelector("[data-announcement-next]");
    var prevBtn = document.querySelector("[data-announcement-prev]");
    if (nextBtn) nextBtn.addEventListener("click", function () { step(1); });
    if (prevBtn) prevBtn.addEventListener("click", function () { step(-1); });
  }

  /* ---------------- Init ---------------- */
  function init() {
    initHeaderScroll();
    initMegaMenu();
    initMobileNav();
    initAccordions();
    initCollectionToolbar();
    initAnnouncementBar();
    initSliders();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", function () {
    initMegaMenu();
    initMobileNav();
    initAccordions();
    initAnnouncementBar();
    initSliders();
  });
  document.addEventListener("velmora:content-updated", function () {
    initSliders();
  });
})();
